import { useCallback, useEffect, useMemo, useState } from "react";
import { calculateNutrientsByAmount, normalizeEditableItem } from "../../helpers";
import DiarySum from "../diary/DiarySum";
import ActionStatusText from "../ui/ActionStatusText";
import ErrorMessage from "../ui/ErrorMessage";
import MealItemEditor from "../../modal/components/MealItemEditor";
import ModalHeader from "../../modal/components/ModalHeader";
import useDeleteFavoriteMeal from "../../hooks/data/meals/useDeleteFavoriteMeal";
import EditNameModal from "./EditNameModal";

// HELPERS

const buildEditableItems = (meal) =>
	meal.items.map((item) => ({
		...item,
		base: {
			kcal: item.kcal,
			protein: item.protein,
			fat: item.fat,
			carbs: item.carbs,
			amount: item.amount,
			unit: item.unit,
		},
	}));

const normalizeNewItem = (item) => {
	const normalized = normalizeEditableItem(item);

	return {
		...normalized,
		id: null,
		is_new: true,
	};
};

// COMPONENT

const MealEditorLayout = ({ meal, onSave, isSaving, isSuccess, isError, error, openDiaryModal }) => {
	const {
		deleteFavoriteMeal,
		isDeleting,
		isSuccess: isDeleteSuccess,
		isError: isDeleteError,
		error: deleteError,
	} = useDeleteFavoriteMeal();

	// STATE

	const [indexToDelete, setIndexToDelete] = useState(null);
	const [isEditNameOpen, setIsEditNameOpen] = useState(false);

	const [editableMeal, setEditableMeal] = useState(() => ({
		...meal,
		items: buildEditableItems(meal),
		name: meal.name,
	}));

	// DERIVED

	const isLocked = isSaving || isDeleting || isSuccess || isDeleteSuccess;

	const isAmountInvalid = useMemo(
		() => editableMeal.items.some((item) => !item.amount || item.amount <= 0),
		[editableMeal.items],
	);

	// HANDLERS

	const handleItemChange = useCallback((itemId, { value, unit }, index) => {
		const amount = Number(value) || 0;

		setEditableMeal((prev) => ({
			...prev,
			items: prev.items.map((item, i) => {
				const isMatch = itemId != null ? item.id === itemId : i === index;

				if (!isMatch) return item;

				const recalculated =
					amount > 0
						? calculateNutrientsByAmount(item.base, amount, unit)
						: { kcal: 0, protein: 0, fat: 0, carbs: 0 };

				return { ...item, amount, unit, ...recalculated };
			}),
		}));
	}, []);

	// HANDLERS

	const handleConfirmNameChange = useCallback((newName) => {
		setEditableMeal((prev) => ({
			...prev,
			name: newName,
		}));
	}, []);

	const removeItemAtIndex = (indexToRemove) => {
		setEditableMeal((prev) => ({
			...prev,
			items: prev.items.filter((_, i) => i !== indexToRemove),
		}));
	};

	const redirectBack = useCallback(() => {
		window.location.href = "/matdagbok/favoritmaltid";
	}, []);

	useEffect(() => {
		if (!isSuccess) return;

		setEditableMeal((prev) => ({
			...prev,
			items: prev.items.map((item) => ({
				...item,
				is_new: false,
			})),
		}));
	}, [isSuccess]);

	useEffect(() => {
		if (!isDeleteSuccess) return;

		// Small delay so success text can render
		const timer = setTimeout(() => {
			redirectBack();
		}, 500);

		return () => clearTimeout(timer);
	}, [isDeleteSuccess]);

	return (
		<section className="meal-editor-layout">
			<header className="meal-editor-header">
				<div className="meal-name-container">
					<h1 className="meal-name">{editableMeal.name}</h1>

					<button
						className="edit-meal-name-btn"
						onClick={() => setIsEditNameOpen(true)}
						disabled={isLocked}
					>
						<i className="fas fa-pencil"></i>
					</button>
				</div>

				<button
					className="btn-green bold-title"
					disabled={isLocked}
					onClick={() =>
						openDiaryModal(`${meal.type}-0`, "default", null, {
							pickerMode: true,
							onPickItems: (items) => {
								setEditableMeal((prev) => ({
									...prev,
									items: [...prev.items, ...items.map(normalizeNewItem)],
								}));
							},
						})
					}
				>
					Lägg till livsmedel
				</button>

				<h3>Redigera favoritmåltid</h3>
			</header>

			{isError && <ErrorMessage message={error?.message || "Ett fel uppstod."} />}

			<div className="meal-editor-items">
				{editableMeal.items.map((item, index) => (
					<div key={item.id ?? `new-${index}`} className="edit-meal-item-wrapper">
						<div className="item-info">
							{item.name}

							<span className="item-kcal">{item.kcal} kcal</span>
						</div>

						<MealItemEditor
							initialValue={item.amount}
							initialUnit={item.unit}
							options={item.units}
							onChangeValue={(data) => handleItemChange(item.id, data, index)}
							onDelete={() => {
								if (editableMeal.items.length === 1) {
									setIndexToDelete(index);
									return;
								}

								removeItemAtIndex(index);
							}}
							isDisabled={isLocked}
							isCustom={!item.food_item_id}
						/>
					</div>
				))}
			</div>

			<div className="meal-editor-footer">
				<DiarySum items={editableMeal.items} />

				<button
					className="btn-green bold-title"
					disabled={isAmountInvalid || isLocked}
					onClick={() => onSave(editableMeal)}
				>
					<ActionStatusText
						defaultLabel="Spara ändringar"
						successLabel="Ändringar har sparats!"
						isProcessing={isSaving}
						isCompleted={isSuccess}
					/>
				</button>

				<button className="marked-border-btn bold-title" onClick={redirectBack} disabled={isLocked}>
					Gå tillbaka
				</button>
			</div>

			{indexToDelete !== null && (
				<div className="favorite-editor-modal-overlay">
					<div className="favorite-editor-modal">
						<ModalHeader
							showBack={false}
							onClose={() => setIndexToDelete(null)}
							title="Radera favoritmåltid"
							disableButtons={isDeleting || isDeleteSuccess}
						/>

						<div className="modal-body delete-confirm">
							{!isDeleteError ? (
								<div className="content-text">
									<p>Är du säker på att du vill radera favoritmåltiden?</p>
								</div>
							) : (
								<ErrorMessage
									message={deleteError?.message || "Något gick fel vid borttagningen."}
								/>
							)}
						</div>

						<div className="modal-footer delete-confirm">
							<button
								className="btn-red"
								onClick={() => deleteFavoriteMeal(editableMeal.id)}
								disabled={isDeleting || isDeleteSuccess}
							>
								<ActionStatusText
									processingLabel="Raderar"
									defaultLabel="Radera"
									successLabel="Måltid raderad"
									isProcessing={isDeleting}
									isCompleted={isDeleteSuccess}
								/>
							</button>

							<button
								className="marked-border-btn"
								onClick={() => setIndexToDelete(null)}
								disabled={isDeleting}
							>
								Avbryt
							</button>
						</div>
					</div>
				</div>
			)}

			{isEditNameOpen && (
				<div className="favorite-editor-modal-overlay">
					<div className="favorite-editor-modal">
						<EditNameModal
							title="Redigera namn"
							initialValue={editableMeal.name}
							isLocked={isLocked}
							onConfirm={handleConfirmNameChange}
							onClose={() => setIsEditNameOpen(false)}
						/>
					</div>
				</div>
			)}
		</section>
	);
};

export default MealEditorLayout;
